/**
 * WorkPanel — Aurora Work 模式（五阶段 AI 自动化执行引擎）
 *
 * 阶段：澄清需求 → 制定计划 → 执行任务 → 验收 → 交付成品
 *
 * WebSocket 双向协议（与 /api/work/ws 通信）：
 *   Client → Server: start | answer | approve_plan | reject_plan | choice
 *   Server → Client: phase | question | plan | task_start | task_log |
 *                    task_done | task_error | error_choice | verify | done | error
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import { DynamicIsland } from './DynamicIsland';
import './WorkPanel.css';

// ============================================================
// 类型
// ============================================================

type WorkPhase =
  | 'idle'
  | 'clarify'
  | 'plan'
  | 'execute'
  | 'verify'
  | 'done'
  | 'error';

interface QAMessage {
  id: string;
  role: 'ai' | 'user';
  content: string;
  questionId?: string;
}

interface PlanTask {
  id: string;
  title: string;
  type: 'code' | 'text' | 'search' | 'terminal' | 'deploy' | 'file' | 'verify';
  description: string;
  model: string;
  deviceId?: string;
  command?: string;
  // runtime state
  status: 'pending' | 'running' | 'done' | 'failed' | 'skipped';
  progress: number;
  logs: LogEntry[];
  result?: string;
}

interface PlanPhase {
  id: string;
  title: string;
  tasks: PlanTask[];
}

interface WorkPlan {
  summary: string;
  phases: PlanPhase[];
}

interface LogEntry {
  id: string;
  text: string;
  stream: 'stdout' | 'stderr' | 'system';
  ts: number;
}

interface ErrorChoice {
  action: 'retry' | 'skip' | 'abort' | 'fix';
  label: string;
  description: string;
}

interface PendingError {
  errorId: string;
  taskTitle: string;
  error: string;
  choices: ErrorChoice[];
}

interface VerifyItem {
  check: string;
  passed: boolean;
  note: string;
}

interface SSHDevice {
  id: string;
  name: string;
  host: string;
  user: string;
  role: string;
}

// ============================================================
// 常量
// ============================================================

const PHASE_STEPS: { key: WorkPhase; label: string }[] = [
  { key: 'clarify', label: '理解需求' },
  { key: 'plan',    label: '制定计划' },
  { key: 'execute', label: '执行任务' },
  { key: 'verify',  label: '验收' },
  { key: 'done',    label: '交付' },
];

const TASK_TYPE_ICONS: Record<PlanTask['type'], string> = {
  code:     '💻',
  text:     '📝',
  search:   '🔍',
  terminal: '⌨️',
  deploy:   '🚀',
  file:     '📄',
  verify:   '✅',
};

const TASK_TYPE_LABELS: Record<PlanTask['type'], string> = {
  code:     '编写代码',
  text:     '生成文本',
  search:   '信息搜索',
  terminal: 'SSH 命令',
  deploy:   '部署上线',
  file:     '文件操作',
  verify:   '验证检查',
};

function genId(p = 'x') {
  return `${p}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

// ============================================================
// 子组件：阶段步进器
// ============================================================

const PhaseStepper: FC<{ phase: WorkPhase }> = ({ phase }) => {
  const activeIdx = PHASE_STEPS.findIndex(s => s.key === phase);
  return (
    <div className="wp-stepper">
      {PHASE_STEPS.map((s, i) => {
        const state =
          phase === 'idle' ? 'pending'
          : i < activeIdx ? 'done'
          : i === activeIdx ? 'active'
          : 'pending';
        return (
          <div key={s.key} className="wp-stepper__item">
            <div className={`wp-stepper__dot wp-stepper__dot--${state}`}>
              {state === 'done' ? '✓' : i + 1}
            </div>
            <span className={`wp-stepper__label wp-stepper__label--${state}`}>{s.label}</span>
            {i < PHASE_STEPS.length - 1 && <div className={`wp-stepper__line wp-stepper__line--${state === 'done' ? 'done' : 'pending'}`} />}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================
// 子组件：输入阶段
// ============================================================

interface InputPhaseProps {
  onStart: (task: string, context: string, deviceId: string) => void;
  devices: SSHDevice[];
}

const InputPhase: FC<InputPhaseProps> = ({ onStart, devices }) => {
  const [task, setTask] = useState('');
  const [context, setContext] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { textareaRef.current?.focus(); }, []);

  const handleStart = () => {
    if (task.trim()) onStart(task.trim(), context.trim(), deviceId);
  };

  return (
    <div className="wp-input-phase">
      <div className="wp-input-phase__hero">
        <div className="wp-input-phase__icon">⚡</div>
        <h2 className="wp-input-phase__title">告诉我你想完成什么</h2>
        <p className="wp-input-phase__sub">AI 会先提问充分了解需求，然后自动制定计划并执行，包括编写代码、部署服务器等</p>
      </div>

      <div className="wp-input-phase__form">
        <div className="wp-input-phase__field">
          <label className="wp-input-phase__label">目标任务</label>
          <textarea
            ref={textareaRef}
            className="wp-input-phase__textarea"
            placeholder="例如：帮我开发一个 Node.js API 服务，包含用户认证，部署到服务器..."
            value={task}
            onChange={e => setTask(e.target.value)}
            rows={5}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleStart();
            }}
          />
        </div>

        <div className="wp-input-phase__field">
          <label className="wp-input-phase__label">补充上下文（可选）</label>
          <textarea
            className="wp-input-phase__textarea wp-input-phase__textarea--sm"
            placeholder="现有代码仓库、技术栈要求、约束条件..."
            value={context}
            onChange={e => setContext(e.target.value)}
            rows={2}
          />
        </div>

        {devices.length > 0 && (
          <div className="wp-input-phase__field">
            <label className="wp-input-phase__label">执行设备（SSH）</label>
            <select
              className="wp-input-phase__select"
              value={deviceId}
              onChange={e => setDeviceId(e.target.value)}
            >
              <option value="">不使用服务器（仅 AI 生成）</option>
              {devices.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name} — {d.user}@{d.host} [{d.role}]
                </option>
              ))}
            </select>
          </div>
        )}

        {devices.length === 0 && (
          <div className="wp-input-phase__no-device">
            <span>💡 在「远程设备中心」配置 SSH 服务器后，Work 模式可自动执行部署命令</span>
          </div>
        )}

        <button
          className="wp-input-phase__start"
          disabled={!task.trim()}
          onClick={handleStart}
        >
          ⚡ 开始执行
          <span className="wp-input-phase__shortcut">Ctrl+↵</span>
        </button>
      </div>
    </div>
  );
};

// ============================================================
// 子组件：澄清阶段（对话式问答）
// ============================================================

interface ClarifyPhaseProps {
  messages: QAMessage[];
  currentQuestion: { id: string; text: string } | null;
  onAnswer: (questionId: string, answer: string) => void;
}

const ClarifyPhase: FC<ClarifyPhaseProps> = ({ messages, currentQuestion, onAnswer }) => {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentQuestion]);

  const handleSend = () => {
    if (currentQuestion && input.trim()) {
      onAnswer(currentQuestion.id, input.trim());
      setInput('');
    }
  };

  return (
    <div className="wp-clarify">
      <div className="wp-clarify__header">
        <div className="wp-clarify__icon">🤔</div>
        <div>
          <div className="wp-clarify__title">需求理解</div>
          <div className="wp-clarify__sub">AI 正在提问以全面了解你的需求，请逐一回答</div>
        </div>
      </div>

      <div className="wp-clarify__messages">
        {messages.map(m => (
          <div key={m.id} className={`wp-clarify__msg wp-clarify__msg--${m.role}`}>
            {m.role === 'ai' && <div className="wp-clarify__avatar">AI</div>}
            <div className="wp-clarify__bubble">{m.content}</div>
          </div>
        ))}

        {currentQuestion && (
          <div className="wp-clarify__msg wp-clarify__msg--ai">
            <div className="wp-clarify__avatar">AI</div>
            <div className="wp-clarify__bubble wp-clarify__bubble--active">
              {currentQuestion.text}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {currentQuestion && (
        <div className="wp-clarify__input-row">
          <textarea
            className="wp-clarify__input"
            placeholder="输入你的回答..."
            value={input}
            onChange={e => setInput(e.target.value)}
            rows={2}
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
            }}
          />
          <button
            className="wp-clarify__send"
            onClick={handleSend}
            disabled={!input.trim()}
          >
            回答 →
          </button>
        </div>
      )}

      {!currentQuestion && messages.length > 0 && (
        <div className="wp-clarify__processing">
          <div className="wp-spinner" />
          <span>AI 正在整理信息，制定执行计划...</span>
        </div>
      )}
    </div>
  );
};

// ============================================================
// 子组件：计划阶段
// ============================================================

interface PlanPhaseProps {
  plan: WorkPlan | null;
  onApprove: () => void;
  onReject: (feedback: string) => void;
  waiting: boolean;
}

const PlanPhaseView: FC<PlanPhaseProps> = ({ plan, onApprove, onReject, waiting }) => {
  const [feedback, setFeedback] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);

  if (!plan) {
    return (
      <div className="wp-plan wp-plan--loading">
        <div className="wp-spinner" />
        <span>AI 正在制定执行计划...</span>
      </div>
    );
  }

  return (
    <div className="wp-plan">
      <div className="wp-plan__header">
        <div className="wp-plan__icon">📋</div>
        <div>
          <div className="wp-plan__title">执行计划</div>
          <div className="wp-plan__summary">{plan.summary}</div>
        </div>
      </div>

      <div className="wp-plan__phases">
        {plan.phases.map((ph, pi) => (
          <div key={ph.id} className="wp-plan__phase">
            <div className="wp-plan__phase-head">
              <span className="wp-plan__phase-num">{pi + 1}</span>
              <span className="wp-plan__phase-title">{ph.title}</span>
              <span className="wp-plan__phase-count">{ph.tasks.length} 个任务</span>
            </div>
            <div className="wp-plan__tasks">
              {ph.tasks.map(t => (
                <div key={t.id} className="wp-plan__task">
                  <span className="wp-plan__task-icon">{TASK_TYPE_ICONS[t.type]}</span>
                  <div className="wp-plan__task-info">
                    <div className="wp-plan__task-title">{t.title}</div>
                    <div className="wp-plan__task-desc">{t.description}</div>
                  </div>
                  <div className="wp-plan__task-meta">
                    <span className="wp-plan__task-type">{TASK_TYPE_LABELS[t.type]}</span>
                    <span className="wp-plan__task-model">{t.model}</span>
                    {t.command && <span className="wp-plan__task-cmd">$ {t.command.slice(0, 30)}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {waiting && (
        <div className="wp-plan__actions">
          {!showFeedback ? (
            <>
              <button className="wp-plan__btn wp-plan__btn--approve" onClick={onApprove}>
                ✓ 批准计划，开始执行
              </button>
              <button className="wp-plan__btn wp-plan__btn--reject" onClick={() => setShowFeedback(true)}>
                ✎ 要求修改
              </button>
            </>
          ) : (
            <div className="wp-plan__feedback">
              <textarea
                className="wp-plan__feedback-input"
                placeholder="说明需要修改的地方..."
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                rows={3}
                autoFocus
              />
              <div className="wp-plan__feedback-btns">
                <button className="wp-plan__btn wp-plan__btn--ghost" onClick={() => setShowFeedback(false)}>取消</button>
                <button
                  className="wp-plan__btn wp-plan__btn--reject"
                  disabled={!feedback.trim()}
                  onClick={() => { onReject(feedback.trim()); setFeedback(''); setShowFeedback(false); }}
                >
                  提交反馈
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================
// 子组件：执行阶段（任务卡片）
// ============================================================

interface ExecutePhaseProps {
  plan: WorkPlan | null;
  overallProgress: number;
  totalTasks: number;
  completedTasks: number;
}

const ExecutePhase: FC<ExecutePhaseProps> = ({ plan, overallProgress, totalTasks, completedTasks }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded(s => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  if (!plan) return null;

  return (
    <div className="wp-execute">
      <div className="wp-execute__header">
        <div className="wp-execute__title">任务执行中</div>
        <div className="wp-execute__counter">{completedTasks} / {totalTasks} 完成</div>
      </div>

      <div className="wp-execute__progress-bar">
        <div className="wp-execute__progress-fill" style={{ width: `${overallProgress * 100}%` }} />
      </div>

      <div className="wp-execute__phases">
        {plan.phases.map(ph => (
          <div key={ph.id} className="wp-execute__phase">
            <div className="wp-execute__phase-title">{ph.title}</div>
            {ph.tasks.map(t => {
              const isOpen = expanded.has(t.id);
              return (
                <div key={t.id} className={`wp-execute__task wp-execute__task--${t.status}`}>
                  <div className="wp-execute__task-head" onClick={() => t.logs.length > 0 && toggle(t.id)}>
                    <div className="wp-execute__task-status-icon">
                      {t.status === 'running' ? <div className="wp-spinner wp-spinner--sm" /> :
                       t.status === 'done'    ? '✓' :
                       t.status === 'failed'  ? '✗' :
                       t.status === 'skipped' ? '⊘' :
                                                TASK_TYPE_ICONS[t.type]}
                    </div>
                    <span className="wp-execute__task-title">{t.title}</span>
                    <span className="wp-execute__task-type">{TASK_TYPE_LABELS[t.type]}</span>
                    {t.status === 'running' && <div className="wp-execute__task-bar"><div className="wp-execute__task-bar-fill wp-execute__task-bar-fill--anim" /></div>}
                    {t.status === 'done'    && <span className="wp-execute__task-done-label">完成</span>}
                    {t.status === 'failed'  && <span className="wp-execute__task-err-label">失败</span>}
                    {t.logs.length > 0 && <span className="wp-execute__task-toggle">{isOpen ? '▲' : '▼'}</span>}
                  </div>

                  {isOpen && t.logs.length > 0 && (
                    <div className="wp-execute__logs">
                      {t.logs.map(l => (
                        <div key={l.id} className={`wp-execute__log wp-execute__log--${l.stream}`}>
                          {l.text}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// 子组件：验收阶段
// ============================================================

const VerifyPhase: FC<{ passed: boolean; items: VerifyItem[] }> = ({ passed, items }) => (
  <div className="wp-verify">
    <div className={`wp-verify__banner wp-verify__banner--${passed ? 'ok' : 'warn'}`}>
      {passed ? '✓ 验收通过' : '⚠ 部分验收项未通过'}
    </div>
    <div className="wp-verify__items">
      {items.map((item, i) => (
        <div key={i} className={`wp-verify__item wp-verify__item--${item.passed ? 'ok' : 'fail'}`}>
          <span className="wp-verify__item-icon">{item.passed ? '✓' : '✗'}</span>
          <div className="wp-verify__item-info">
            <div className="wp-verify__item-check">{item.check}</div>
            {item.note && <div className="wp-verify__item-note">{item.note}</div>}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============================================================
// 子组件：右侧实时日志
// ============================================================

interface LiveLogProps {
  logs: LogEntry[];
  pendingError: PendingError | null;
  onChoice: (errorId: string, action: string) => void;
}

const LiveLog: FC<LiveLogProps> = ({ logs, pendingError, onChoice }) => {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs.length, pendingError]);

  return (
    <div className="wp-livelog">
      <div className="wp-livelog__header">实时进度</div>
      <div className="wp-livelog__body">
        {logs.map(l => (
          <div key={l.id} className={`wp-livelog__line wp-livelog__line--${l.stream}`}>
            <span className="wp-livelog__time">
              {new Date(l.ts).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
            <span className="wp-livelog__text">{l.text}</span>
          </div>
        ))}

        {pendingError && (
          <div className="wp-livelog__error-card">
            <div className="wp-livelog__error-title">⚠ {pendingError.taskTitle} — 需要你的决策</div>
            <div className="wp-livelog__error-msg">{pendingError.error.slice(0, 200)}</div>
            <div className="wp-livelog__error-choices">
              {pendingError.choices.map(c => (
                <button
                  key={c.action}
                  className={`wp-livelog__choice wp-livelog__choice--${c.action}`}
                  onClick={() => onChoice(pendingError.errorId, c.action)}
                  title={c.description}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div ref={endRef} />
      </div>
    </div>
  );
};

// ============================================================
// 轻量 Markdown 渲染
// ============================================================

function renderMarkdown(md: string): string {
  if (!md) return '';
  return md
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/^---$/gm, '<hr>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/^(?!<[h|u|l|h|c|p])(.+)$/gm, '<p>$1</p>');
}

// ============================================================
// 主组件
// ============================================================

interface WorkPanelProps {
  model: string;
}

const WorkPanel: FC<WorkPanelProps> = ({ model }) => {
  // ── 状态 ──────────────────────────────────────────────────
  const [phase, setPhase] = useState<WorkPhase>('idle');
  const [phaseMsg, setPhaseMsg] = useState('');
  const [qaMessages, setQaMessages] = useState<QAMessage[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<{ id: string; text: string } | null>(null);
  const [plan, setPlan] = useState<WorkPlan | null>(null);
  const [waitingPlanApproval, setWaitingPlanApproval] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const [totalTasks, setTotalTasks] = useState(0);
  const [completedTasks, setCompletedTasks] = useState(0);
  const [verifyResult, setVerifyResult] = useState<{ passed: boolean; items: VerifyItem[] } | null>(null);
  const [finalResult, setFinalResult] = useState<string | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [liveLogs, setLiveLogs] = useState<LogEntry[]>([]);
  const [pendingError, setPendingError] = useState<PendingError | null>(null);
  const [devices, setDevices] = useState<SSHDevice[]>([]);

  const wsRef = useRef<WebSocket | null>(null);

  // ── 加载 SSH 设备 ──────────────────────────────────────────
  useEffect(() => {
    fetch('/api/ssh-devices')
      .then(r => r.json())
      .then(d => d.success && setDevices(d.data))
      .catch(() => {});
  }, []);

  // ── WebSocket 管理 ─────────────────────────────────────────
  const wsSend = useCallback((msg: object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const addLog = useCallback((text: string, stream: LogEntry['stream'] = 'system') => {
    setLiveLogs(l => [...l, { id: genId('log'), text, stream, ts: Date.now() }]);
  }, []);

  // Updates a task's field inside the plan
  const updateTask = useCallback((taskId: string, patch: Partial<PlanTask>) => {
    setPlan(p => {
      if (!p) return p;
      return {
        ...p,
        phases: p.phases.map(ph => ({
          ...ph,
          tasks: ph.tasks.map(t => t.id === taskId ? { ...t, ...patch } : t),
        })),
      };
    });
  }, []);

  const appendTaskLog = useCallback((taskId: string, log: LogEntry) => {
    setPlan(p => {
      if (!p) return p;
      return {
        ...p,
        phases: p.phases.map(ph => ({
          ...ph,
          tasks: ph.tasks.map(t =>
            t.id === taskId ? { ...t, logs: [...t.logs, log] } : t
          ),
        })),
      };
    });
  }, []);

  // ── Websocket 消息处理 ──────────────────────────────────────
  const handleWsMessage = useCallback((raw: string) => {
    let msg: any;
    try { msg = JSON.parse(raw); } catch { return; }

    switch (msg.type) {
      case 'phase':
        setPhase(msg.phase);
        setPhaseMsg(msg.message || '');
        addLog(`[阶段] ${msg.message || msg.phase}`, 'system');
        if (msg.phase === 'execute') {
          DynamicIsland.show({ type: 'progress', title: 'Work 任务执行中', progress: 0.05, duration: 0 });
        }
        break;

      case 'question':
        setCurrentQuestion({ id: msg.id, text: msg.text });
        break;

      case 'plan':
        setPlan({
          ...msg.plan,
          phases: msg.plan.phases.map((ph: any) => ({
            ...ph,
            tasks: ph.tasks.map((t: any) => ({
              ...t,
              status: 'pending' as const,
              progress: 0,
              logs: [],
            })),
          })),
        });
        break;

      case 'await_plan_approval':
        setWaitingPlanApproval(true);
        break;

      case 'task_start':
        setTotalTasks(msg.totalTasks || 0);
        setCompletedTasks(msg.completedTasks || 0);
        updateTask(msg.taskId, { status: 'running' });
        addLog(`▶ ${msg.phaseTitle} / ${msg.title}`, 'system');
        DynamicIsland.show({
          type: 'progress',
          title: msg.title,
          body: msg.phaseTitle,
          progress: (msg.completedTasks || 0) / Math.max(msg.totalTasks || 1, 1),
          duration: 0,
        });
        break;

      case 'task_log': {
        const logEntry: LogEntry = { id: genId('l'), text: msg.log, stream: msg.stream, ts: Date.now() };
        appendTaskLog(msg.taskId, logEntry);
        addLog(msg.log, msg.stream);
        break;
      }

      case 'task_done':
        setCompletedTasks(msg.completedTasks || 0);
        setOverallProgress(msg.progress || 0);
        updateTask(msg.taskId, { status: 'done', result: msg.result });
        DynamicIsland.show({
          type: 'progress',
          title: '任务完成',
          progress: msg.progress || 0,
          duration: 0,
        });
        break;

      case 'task_error':
        setOverallProgress(msg.progress || 0);
        updateTask(msg.taskId, { status: 'failed' });
        addLog(`✗ ${msg.error}`, 'stderr');
        break;

      case 'error_choice':
        setPendingError({
          errorId: msg.errorId,
          taskTitle: msg.taskTitle,
          error: msg.error,
          choices: msg.choices,
        });
        DynamicIsland.show({ type: 'error', title: `任务出错: ${msg.taskTitle}`, body: msg.error?.slice(0, 50), duration: 0 });
        break;

      case 'verify':
        setVerifyResult({ passed: msg.passed, items: msg.items || [] });
        break;

      case 'done':
        setFinalResult(msg.result);
        DynamicIsland.show({ type: 'notification', title: 'Work 任务完成', body: '所有任务已交付', icon: '✅', duration: 5000 });
        break;

      case 'error':
        setGlobalError(msg.error);
        setPhase('error');
        DynamicIsland.show({ type: 'error', title: 'Work 任务失败', body: msg.error?.slice(0, 60), duration: 6000 });
        break;
    }
  }, [addLog, updateTask, appendTaskLog]);

  // ── 开始工作流 ─────────────────────────────────────────────
  const handleStart = useCallback((task: string, context: string, deviceId: string) => {
    setPhase('clarify');
    setQaMessages([]);
    setCurrentQuestion(null);
    setPlan(null);
    setWaitingPlanApproval(false);
    setOverallProgress(0);
    setVerifyResult(null);
    setFinalResult(null);
    setGlobalError(null);
    setLiveLogs([]);
    setPendingError(null);

    addLog('建立 WebSocket 连接...', 'system');
    DynamicIsland.show({ type: 'thinking', title: 'Work 任务启动中', body: task.slice(0, 40), duration: 0 });

    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${window.location.host}/api/work/ws`);
    wsRef.current = ws;

    ws.onopen = () => {
      addLog('连接已建立', 'system');
      ws.send(JSON.stringify({ type: 'start', task, context, deviceId: deviceId || undefined, model }));
    };

    ws.onmessage = e => handleWsMessage(e.data);

    ws.onerror = () => addLog('WebSocket 连接错误', 'stderr');

    ws.onclose = () => addLog('连接已关闭', 'system');
  }, [model, addLog, handleWsMessage]);

  // ── 回答问题 ───────────────────────────────────────────────
  const handleAnswer = useCallback((questionId: string, answer: string) => {
    if (!currentQuestion) return;
    setQaMessages(m => [
      ...m,
      { id: genId('qa-ai'), role: 'ai', content: currentQuestion.text },
      { id: genId('qa-u'),  role: 'user', content: answer },
    ]);
    setCurrentQuestion(null);
    wsSend({ type: 'answer', questionId, answer });
    addLog(`用户回答: ${answer.slice(0, 60)}`, 'system');
  }, [currentQuestion, wsSend, addLog]);

  // ── 计划审批 ───────────────────────────────────────────────
  const handleApprove = useCallback(() => {
    setWaitingPlanApproval(false);
    wsSend({ type: 'approve_plan' });
    addLog('用户批准计划', 'system');
  }, [wsSend, addLog]);

  const handleReject = useCallback((feedback: string) => {
    setWaitingPlanApproval(false);
    wsSend({ type: 'reject_plan', feedback });
    addLog(`用户要求修改: ${feedback.slice(0, 60)}`, 'system');
  }, [wsSend, addLog]);

  // ── 错误选择 ───────────────────────────────────────────────
  const handleChoice = useCallback((errorId: string, action: string) => {
    setPendingError(null);
    wsSend({ type: 'choice', errorId, action });
    addLog(`用户选择: ${action}`, 'system');
    if (action !== 'abort') {
      DynamicIsland.show({ type: 'progress', title: '继续执行...', progress: overallProgress, duration: 0 });
    }
  }, [wsSend, addLog, overallProgress]);

  // ── 重置 ───────────────────────────────────────────────────
  const handleReset = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
    setPhase('idle');
    setQaMessages([]);
    setCurrentQuestion(null);
    setPlan(null);
    setWaitingPlanApproval(false);
    setOverallProgress(0);
    setVerifyResult(null);
    setFinalResult(null);
    setGlobalError(null);
    setLiveLogs([]);
    setPendingError(null);
  }, []);

  // ── 渲染 ───────────────────────────────────────────────────
  const showLog = phase !== 'idle';

  return (
    <div className="wp">
      {/* 顶栏 */}
      <div className="wp__topbar">
        <PhaseStepper phase={phase} />
        {phase !== 'idle' && (
          <button className="wp__reset-btn" onClick={handleReset}>
            ↺ 新任务
          </button>
        )}
      </div>

      {/* 状态消息 */}
      {phaseMsg && phase !== 'idle' && phase !== 'done' && (
        <div className="wp__phase-msg">
          <div className="wp-spinner wp-spinner--sm" />
          {phaseMsg}
        </div>
      )}

      {/* 主体 */}
      <div className={`wp__body ${showLog ? 'wp__body--split' : ''}`}>
        {/* 左侧：主内容 */}
        <div className="wp__main">
          {phase === 'idle' && (
            <InputPhase onStart={handleStart} devices={devices} />
          )}
          {(phase === 'clarify') && (
            <ClarifyPhase
              messages={qaMessages}
              currentQuestion={currentQuestion}
              onAnswer={handleAnswer}
            />
          )}
          {(phase === 'plan' || (phase === 'execute' && !plan)) && (
            <PlanPhaseView
              plan={plan}
              onApprove={handleApprove}
              onReject={handleReject}
              waiting={waitingPlanApproval}
            />
          )}
          {phase === 'execute' && plan && (
            <ExecutePhase
              plan={plan}
              overallProgress={overallProgress}
              totalTasks={totalTasks}
              completedTasks={completedTasks}
            />
          )}
          {phase === 'verify' && verifyResult && (
            <VerifyPhase passed={verifyResult.passed} items={verifyResult.items} />
          )}
          {phase === 'done' && finalResult && (
            <div className="wp-done">
              <div className="wp-done__header">
                <span className="wp-done__icon">🎉</span>
                <div>
                  <div className="wp-done__title">任务完成，成品已交付</div>
                  {verifyResult && (
                    <div className={`wp-done__verify wp-done__verify--${verifyResult.passed ? 'ok' : 'warn'}`}>
                      验收：{verifyResult.passed ? '全部通过' : '部分未通过，请检查'}
                    </div>
                  )}
                </div>
              </div>
              <div
                className="wp-done__result"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(finalResult) }}
              />
              <button className="wp-done__new-btn" onClick={handleReset}>↺ 开始新任务</button>
            </div>
          )}
          {phase === 'error' && (
            <div className="wp-error-screen">
              <div className="wp-error-screen__icon">⚠</div>
              <div className="wp-error-screen__title">任务执行失败</div>
              <div className="wp-error-screen__msg">{globalError}</div>
              <button className="wp-error-screen__btn" onClick={handleReset}>重新开始</button>
            </div>
          )}
        </div>

        {/* 右侧：实时日志 */}
        {showLog && (
          <LiveLog
            logs={liveLogs}
            pendingError={pendingError}
            onChoice={handleChoice}
          />
        )}
      </div>
    </div>
  );
};

export default WorkPanel;
